/**
 * OrbitalCarousel - "Intimate Intelligence" Mission Feed
 *
 * A 3D orbital carousel where missions orbit around the user (YOU at center).
 * The most relevant mission is "pulled forward" and floats above the orbit.
 *
 * Design Philosophy:
 * - You are the center of your data universe
 * - Missions orbit around YOU, not the other way around
 * - The "surfaced" mission breaks free, demanding attention
 * - Warm paper aesthetic with intelligent gold accents
 */

import { useState, useCallback } from 'react';
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useSpring,
  PanInfo,
} from 'framer-motion';

// ============================================================================
// DESIGN TOKENS - "Intimate Intelligence" System
// ============================================================================

const tokens = {
  colors: {
    // Base (The Paper)
    background: '#FAF8F5', // Warm Ivory
    surface: '#FFFFFF', // True White
    border: '#E8E4DF', // Soft Edge
    text: '#2C2C2C', // Soft Black
    textMuted: '#6B7280', // Muted Gray

    // Intelligence (The Ink)
    primary: '#4A5568', // Slate
    accent: '#B8860B', // Burnished Gold
    accentGlow: 'rgba(184, 134, 11, 0.15)', // Gold Glow
    highlight: '#FFF8E7', // Gold Highlight

    // Center glow
    centerGlow: 'rgba(184, 134, 11, 0.3)',
  },
  fonts: {
    display: '"Fraunces", Georgia, serif',
    body: '"DM Sans", system-ui, sans-serif',
    mono: '"JetBrains Mono", monospace',
  },
  shadows: {
    card: '0 4px 20px rgba(0, 0, 0, 0.08)',
    surfaced: '0 20px 60px rgba(0, 0, 0, 0.15)',
    glow: '0 0 40px rgba(184, 134, 11, 0.2)',
  },
};

// ============================================================================
// TYPES
// ============================================================================

export interface Mission {
  id: string;
  emoji: string;
  title: string;
  subtitle: string;
  detail?: string;
  matchPercent: number;
  insight?: string;
}

interface OrbitalCarouselProps {
  missions: Mission[];
  userName?: string;
  onMissionAction?: (mission: Mission, action: 'accept' | 'dismiss') => void;
}

// ============================================================================
// SAMPLE DATA
// ============================================================================

const sampleMissions: Mission[] = [
  {
    id: '1',
    emoji: '🎫',
    title: 'The National',
    subtitle: 'Greek Theatre · Mar 15',
    detail: '$65',
    matchPercent: 94,
    insight: "You've seen them twice before and loved it.",
  },
  {
    id: '2',
    emoji: '✈️',
    title: 'Tokyo Trip',
    subtitle: '23% off flights',
    matchPercent: 87,
  },
  {
    id: '3',
    emoji: '☕',
    title: 'Coffee Sub',
    subtitle: 'Blue Bottle · $12/mo',
    matchPercent: 82,
  },
  {
    id: '4',
    emoji: '📚',
    title: 'Book Club',
    subtitle: 'Sci-fi readers nearby',
    matchPercent: 76,
  },
  {
    id: '5',
    emoji: '🔋',
    title: 'GreenEnergy',
    subtitle: 'Save $47/year',
    matchPercent: 71,
  },
  {
    id: '6',
    emoji: '🏃',
    title: 'Morning Run',
    subtitle: 'Group near you',
    matchPercent: 68,
  },
];

// ============================================================================
// ORBITAL CARD COMPONENT
// ============================================================================

interface OrbitCardProps {
  mission: Mission;
  angle: number;
  onClick: () => void;
  orbitRadius: number;
}

function OrbitCard({ mission, angle, onClick, orbitRadius }: OrbitCardProps) {
  // Calculate 3D position
  const x = Math.sin((angle * Math.PI) / 180) * orbitRadius;
  const z = Math.cos((angle * Math.PI) / 180) * orbitRadius;
  const scale = (z + orbitRadius) / (orbitRadius * 2); // 0.5 to 1 based on depth
  const opacity = 0.4 + scale * 0.6;

  return (
    <motion.div
      onClick={onClick}
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{
        x,
        z,
        scale: scale * 0.8 + 0.2,
        opacity,
        rotateY: -angle,
      }}
      transition={{
        type: 'spring',
        stiffness: 100,
        damping: 20,
      }}
      whileHover={{ scale: scale * 0.9 + 0.2 }}
      whileTap={{ scale: scale * 0.75 + 0.2 }}
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        marginLeft: -50,
        marginTop: -60,
        width: 100,
        height: 120,
        cursor: 'pointer',
        transformStyle: 'preserve-3d',
        zIndex: Math.round(scale * 100),
      }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          background: tokens.colors.surface,
          borderRadius: 16,
          border: `1px solid ${tokens.colors.border}`,
          boxShadow: tokens.shadows.card,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 12,
          fontFamily: tokens.fonts.body,
        }}
      >
        <span style={{ fontSize: 32, marginBottom: 8 }}>{mission.emoji}</span>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: tokens.colors.text,
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          {mission.title}
        </span>
        <span
          style={{
            fontSize: 10,
            color: tokens.colors.textMuted,
            textAlign: 'center',
            marginTop: 4,
          }}
        >
          {mission.subtitle.split('·')[0]}
        </span>
      </div>
    </motion.div>
  );
}

// ============================================================================
// SURFACED CARD COMPONENT
// ============================================================================

interface SurfacedCardProps {
  mission: Mission;
  onDismiss: () => void;
  onAccept: () => void;
}

function SurfacedCard({ mission, onDismiss, onAccept }: SurfacedCardProps) {
  return (
    <motion.div
      initial={{ y: 100, opacity: 0, scale: 0.8 }}
      animate={{ y: 0, opacity: 1, scale: 1 }}
      exit={{ y: -50, opacity: 0, scale: 0.9 }}
      transition={{
        type: 'spring',
        stiffness: 200,
        damping: 25,
      }}
      style={{
        width: '100%',
        maxWidth: 320,
        background: tokens.colors.surface,
        borderRadius: 24,
        boxShadow: `${tokens.shadows.surfaced}, ${tokens.shadows.glow}`,
        padding: 24,
        fontFamily: tokens.fonts.body,
        position: 'relative',
      }}
    >
      {/* Gold accent bar */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 24,
          right: 24,
          height: 3,
          background: `linear-gradient(90deg, transparent, ${tokens.colors.accent}, transparent)`,
          borderRadius: '0 0 2px 2px',
        }}
      />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
        <span style={{ fontSize: 48 }}>{mission.emoji}</span>
        <div style={{ flex: 1 }}>
          <h2
            style={{
              margin: 0,
              fontSize: 24,
              fontFamily: tokens.fonts.display,
              fontWeight: 600,
              color: tokens.colors.text,
              lineHeight: 1.2,
            }}
          >
            {mission.title}
          </h2>
          <p
            style={{
              margin: '4px 0 0',
              fontSize: 14,
              color: tokens.colors.textMuted,
            }}
          >
            {mission.subtitle}
          </p>
        </div>
      </div>

      {/* Insight */}
      {mission.insight && (
        <div
          style={{
            background: tokens.colors.highlight,
            borderRadius: 12,
            padding: 12,
            marginBottom: 16,
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: 14,
              fontStyle: 'italic',
              color: tokens.colors.text,
              lineHeight: 1.4,
            }}
          >
            "{mission.insight}"
          </p>
        </div>
      )}

      {/* Match indicator */}
      <div style={{ marginBottom: 20 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 6,
          }}
        >
          <span style={{ fontSize: 12, color: tokens.colors.textMuted }}>Match confidence</span>
          <span
            style={{
              fontSize: 14,
              fontFamily: tokens.fonts.mono,
              fontWeight: 600,
              color: tokens.colors.accent,
            }}
          >
            {mission.matchPercent}%
          </span>
        </div>
        <div
          style={{
            height: 6,
            background: tokens.colors.border,
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${mission.matchPercent}%` }}
            transition={{ delay: 0.3, duration: 0.6, ease: 'easeOut' }}
            style={{
              height: '100%',
              background: `linear-gradient(90deg, ${tokens.colors.accent}, #D4A855)`,
              borderRadius: 3,
            }}
          />
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 12 }}>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onAccept}
          style={{
            flex: 1,
            padding: '14px 20px',
            background: tokens.colors.text,
            color: tokens.colors.surface,
            border: 'none',
            borderRadius: 12,
            fontSize: 15,
            fontWeight: 600,
            fontFamily: tokens.fonts.body,
            cursor: 'pointer',
          }}
        >
          Take Action
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onDismiss}
          style={{
            padding: '14px 20px',
            background: 'transparent',
            color: tokens.colors.textMuted,
            border: `1px solid ${tokens.colors.border}`,
            borderRadius: 12,
            fontSize: 15,
            fontWeight: 500,
            fontFamily: tokens.fonts.body,
            cursor: 'pointer',
          }}
        >
          Not for me
        </motion.button>
      </div>

      {/* Connection line to orbit */}
      <div
        style={{
          position: 'absolute',
          bottom: -30,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 2,
          height: 30,
          background: `linear-gradient(to bottom, ${tokens.colors.border}, transparent)`,
        }}
      />
    </motion.div>
  );
}

// ============================================================================
// CENTER GLOW COMPONENT
// ============================================================================

function CenterGlow() {
  return (
    <motion.div
      animate={{
        scale: [1, 1.1, 1],
        opacity: [0.5, 0.8, 0.5],
      }}
      transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: 60,
        height: 60,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${tokens.colors.centerGlow} 0%, transparent 70%)`,
        pointerEvents: 'none',
      }}
    />
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function OrbitalCarousel({
  missions = sampleMissions,
  userName = 'there',
  onMissionAction,
}: OrbitalCarouselProps) {
  const [surfacedIndex, setSurfacedIndex] = useState(0);
  const [orbitMissions, setOrbitMissions] = useState(missions.slice(1));

  const rotationAngle = useMotionValue(0);
  const springRotation = useSpring(rotationAngle, { stiffness: 100, damping: 30 });

  const surfacedMission = missions[surfacedIndex];
  const orbitRadius = 140;

  // Handle drag to rotate orbit
  const handleDrag = useCallback(
    (_: unknown, info: PanInfo) => {
      const newAngle = rotationAngle.get() + info.delta.x * 0.5;
      rotationAngle.set(newAngle);
    },
    [rotationAngle]
  );

  // Handle card selection from orbit
  const handleSelectFromOrbit = useCallback(
    (index: number) => {
      const selectedMission = orbitMissions[index];
      const newOrbitMissions = [...orbitMissions];
      newOrbitMissions[index] = surfacedMission;
      setOrbitMissions(newOrbitMissions);
      setSurfacedIndex(missions.findIndex((m) => m.id === selectedMission.id));
    },
    [orbitMissions, surfacedMission, missions]
  );

  // Handle dismiss
  const handleDismiss = useCallback(() => {
    if (onMissionAction) {
      onMissionAction(surfacedMission, 'dismiss');
    }
    // Rotate to next mission
    if (orbitMissions.length > 0) {
      const nextMission = orbitMissions[0];
      const newOrbitMissions = [...orbitMissions.slice(1), surfacedMission];
      setOrbitMissions(newOrbitMissions);
      setSurfacedIndex(missions.findIndex((m) => m.id === nextMission.id));
    }
  }, [onMissionAction, surfacedMission, orbitMissions, missions]);

  // Handle accept
  const handleAccept = useCallback(() => {
    if (onMissionAction) {
      onMissionAction(surfacedMission, 'accept');
    }
  }, [onMissionAction, surfacedMission]);

  // Get greeting based on time
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: tokens.colors.background,
        fontFamily: tokens.fonts.body,
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            border: `2px solid ${tokens.colors.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            color: tokens.colors.textMuted,
          }}
        >
          ○
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 14px',
            background: tokens.colors.surface,
            borderRadius: 20,
            boxShadow: tokens.shadows.card,
          }}
        >
          <span style={{ color: tokens.colors.accent }}>◉</span>
          <span
            style={{
              fontFamily: tokens.fonts.mono,
              fontSize: 14,
              fontWeight: 600,
              color: tokens.colors.text,
            }}
          >
            1,247
          </span>
        </div>
      </header>

      {/* Greeting */}
      <h1
        style={{
          fontFamily: tokens.fonts.display,
          fontSize: 28,
          fontWeight: 400,
          color: tokens.colors.text,
          margin: '0 0 30px',
        }}
      >
        {getGreeting()}, {userName}
      </h1>

      {/* Surfaced Card */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: 40,
        }}
      >
        <AnimatePresence mode="wait">
          <SurfacedCard
            key={surfacedMission.id}
            mission={surfacedMission}
            onDismiss={handleDismiss}
            onAccept={handleAccept}
          />
        </AnimatePresence>
      </div>

      {/* Orbit Container */}
      <motion.div
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0}
        onDrag={handleDrag}
        style={{
          position: 'relative',
          height: 280,
          perspective: 800,
          perspectiveOrigin: '50% 50%',
          cursor: 'grab',
        }}
        whileDrag={{ cursor: 'grabbing' }}
      >
        {/* Center YOU indicator */}
        <CenterGlow />
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: tokens.colors.surface,
            border: `2px solid ${tokens.colors.accent}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 11,
            fontWeight: 600,
            color: tokens.colors.accent,
            fontFamily: tokens.fonts.mono,
            zIndex: 50,
          }}
        >
          YOU
        </div>

        {/* Orbit track (visual guide) */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: orbitRadius * 2,
            height: orbitRadius * 2,
            borderRadius: '50%',
            border: `1px dashed ${tokens.colors.border}`,
            opacity: 0.5,
          }}
        />

        {/* Orbiting cards */}
        <motion.div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: 0,
            height: 0,
            transformStyle: 'preserve-3d',
            rotateY: springRotation,
          }}
        >
          {orbitMissions.map((mission, index) => {
            const baseAngle = (360 / orbitMissions.length) * index;
            return (
              <OrbitCard
                key={mission.id}
                mission={mission}
                angle={baseAngle}
                onClick={() => handleSelectFromOrbit(index)}
                orbitRadius={orbitRadius}
              />
            );
          })}
        </motion.div>
      </motion.div>

      {/* Hint */}
      <p
        style={{
          textAlign: 'center',
          color: tokens.colors.textMuted,
          fontSize: 13,
          marginTop: 20,
        }}
      >
        ← Drag to browse your orbit →
      </p>
    </div>
  );
}

export default OrbitalCarousel;
